import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import { supabase } from "../../../supabaseClient";
import OrganizationLanding from "../OrganizationLanding";
import {
  AlertBox,
  SecondaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import { useDpaAcceptance } from "../../../hooks/useDpaAcceptance";
import DPAAcceptanceCard from "../DPAAcceptanceCard/DPAAcceptanceCard";
import {
  clearOrganizationFirstSetup,
  getOrganizationFirstSetup,
} from "../../../utility/organizationFirstSetup";
import { downloadYearTotalsCsv } from "../../../utility/directorMoney";
import {
  combineStatements,
  tournamentStatement,
} from "../../../utility/financeYear";
import {
  currentFiscalYear,
  fiscalYearLabel,
  organizationYearFromRpc,
  tournamentYears,
} from "../../../utility/organizationYear";
import YearAttentionStrip from "./YearAttentionStrip";
import YearTournamentBoard from "./YearTournamentBoard";
import YearFinanceBlock from "./YearFinanceBlock";

const currentYear = currentFiscalYear;

const OrganizationDashboard = () => {
  const { organizations, loading, error, currentOrganization } =
    useOrganization();
  const { canManageTournaments, canManageOrganization } = usePermissions();
  const { hasAccepted, loading: dpaLoading } = useDpaAcceptance(
    currentOrganization?.id
  );
  const [year, setYear] = useState(currentYear);
  const [years, setYears] = useState([currentYear()]);
  const [rollup, setRollup] = useState(null);
  const [yearFinance, setYearFinance] = useState(null);
  const [yearLoading, setYearLoading] = useState(true);
  const [yearError, setYearError] = useState(null);

  useEffect(() => {
    const setup = getOrganizationFirstSetup();
    if (
      setup &&
      organizations?.length > 0 &&
      !organizations.some((org) => org.id === setup.organizationId)
    ) {
      clearOrganizationFirstSetup();
    }
  }, [organizations]);

  useEffect(() => {
    if (!currentOrganization?.id) return undefined;
    let cancelled = false;

    const load = async () => {
      setYearLoading(true);
      setYearError(null);
      setYearFinance(null);
      try {
        const { data, error: tournamentError } = await supabase
          .from("tournaments")
          .select("startDate, squads(squadDate, isDeleted)")
          .eq("organizationID", currentOrganization.id)
          .is("isDeleted", false);

        if (tournamentError) throw tournamentError;
        const list = data || [];
        const availableYears = tournamentYears(list);
        if (!cancelled) setYears(availableYears);

        const { data: yearPayload, error: yearRpcError } = await supabase.rpc(
          "get_organization_year",
          {
            p_organization_id: currentOrganization.id,
            p_year: year,
          }
        );
        if (yearRpcError) throw yearRpcError;
        const next = organizationYearFromRpc(yearPayload);
        if (!cancelled) {
          setRollup(next.rollup);
          setYearFinance(next.finance);
        }
      } catch (loadError) {
        if (!cancelled) {
          setRollup(null);
          setYearError(
            loadError.message || "Could not load this year's totals."
          );
        }
      } finally {
        if (!cancelled) setYearLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentOrganization?.id, year]);

  if (loading) {
    return (
      <div
        className="space-y-6"
        role="status"
        aria-label="Loading organization"
      >
        <SkeletonBoard rows={2} />
        <SkeletonBoard rows={2} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">
        Error loading organization: {error}
      </div>
    );
  }

  if (!organizations || organizations.length === 0) {
    return <OrganizationLanding />;
  }

  const setup = getOrganizationFirstSetup();
  const setupMatchesOrg =
    setup && organizations.some((org) => org.id === setup.organizationId);

  if (setupMatchesOrg) {
    return <OrganizationLanding />;
  }

  const organization = currentOrganization || organizations[0];

  return (
    <div className="space-y-6">
      {currentOrganization &&
        !dpaLoading &&
        !hasAccepted &&
        canManageOrganization && (
          <DPAAcceptanceCard organizationId={currentOrganization?.id} />
        )}

      <div className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                {organization.name}
              </h1>
              <p className="text-sm text-brand-mist mt-1">
                This year's tournaments and payments.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-sm text-brand-mist" htmlFor="org-year">
                Season
              </label>
              <select
                id="org-year"
                value={year}
                onChange={(event) => setYear(Number(event.target.value))}
                className="min-h-11 px-3 py-2 rounded-lg bg-brand-cream text-brand-charcoal text-sm border border-brand-cream"
              >
                {years.map((option) => (
                  <option key={option} value={option}>
                    {fiscalYearLabel(option)}
                  </option>
                ))}
              </select>
              <SecondaryButton
                onClick={() =>
                  downloadYearTotalsCsv({
                    year: fiscalYearLabel(year),
                    rollup,
                    finance: yearFinance,
                    organizationName: organization.name,
                  })
                }
                disabled={!rollup || yearLoading}
                className="!px-3 !py-2 text-sm"
              >
                Download CSV
              </SecondaryButton>
              {canManageTournaments && (
                <Link
                  to="/create-tournament"
                  className="inline-flex items-center min-h-11 bg-brand-gold text-brand-charcoal px-3 py-2 rounded-lg text-sm font-display font-bold tracking-wide hover:bg-brand-gold/90"
                >
                  <FaPlus className="mr-2 h-4 w-4" />
                  Create Tournament
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {yearError ? (
        <AlertBox type="error" title="Error" message={yearError} />
      ) : (
        <YearFinanceBlock
          year={fiscalYearLabel(year)}
          loading={yearLoading}
          rollup={rollup}
          finance={yearFinance}
          statement={
            rollup
              ? combineStatements(
                  (rollup.tournaments || []).map((tournament) =>
                    tournamentStatement(
                      tournament.moneyIn,
                      (yearFinance?.tournaments || []).find(
                        (fees) => fees.id === tournament.id
                      ) || null
                    )
                  )
                )
              : null
          }
        />
      )}

      {yearError || yearLoading ? null : (
        <YearAttentionStrip rollup={rollup} />
      )}

      {yearError ? null : (
        <YearTournamentBoard
          tournaments={rollup?.tournaments || []}
          year={fiscalYearLabel(year)}
          loading={yearLoading}
        />
      )}
    </div>
  );
};

export default OrganizationDashboard;
